import { PropTypes } from 'prop-types';
import {
    getFileExtInfo,
    getExtFromFileName,
    resolveAttachIconSize,
    resolveAttachBadgeMetrics,
    ATTACH_BADGE
} from './AttachUtils';

// Same font for the badge label and the file-name text; single source in ATTACH_BADGE.
const FONT_FAMILY = ATTACH_BADGE.FONT_FAMILY;

/**
 * File-type thumbnail icon for the attachment card.
 *
 * Cloned from AttachFileIcon so changes here do NOT affect the original icon used
 * elsewhere. Differences:
 * - The "Attached File" placeholder text is removed.
 * - The paper area shows the FILE NAME (end-ellipsis when too long).
 * - The top-left badge shows the RAW file extension (e.g. "docx") rendered as SVG; the
 *   badge color is still grouped by file type.
 *
 * API: the caller only passes `fileName` (the component derives the extension, badge label
 * and color from it) and an optional `size` preset ('sm' | 'md' | 'lg' or a raw numeric
 * height); the width is derived to keep the aspect ratio. Image files are NOT rendered
 * through this component (the caller shows an <img>).
 */
function AttachFileThumbnailIcon({ fileName = '', size = 'md', classWrap = '', badgeText, badgeColor }) {
    // Raw file extension (e.g. "docx", "xlsx"); empty when the name has no extension.
    const rawExt = getExtFromFileName(fileName);
    // fileInfo drives the badge COLOR (grouped by type), not the label.
    const fileInfo = getFileExtInfo(rawExt);
    // The caller may override the badge (e.g. a page-knowledge file shows "page" in brown
    // instead of the extension-derived color).
    const extColor = badgeColor || fileInfo.COLOR_DISPLAY;
    // Spec: show the RAW extension, not the grouped type label. Fall back to the group's
    // DISPLAY ("others") only when the file has no real extension to show.
    const extText = badgeText || rawExt || fileInfo.DISPLAY;
    const { width, height } = resolveAttachIconSize(size);

    // Badge metrics shared with the image-preview badge; here it's drawn inside the 56x40
    // viewBox, so only `scale` (SVG transform) and viewBox-unit `fontSize` are used.
    const { scale: badgeScale, fontSize: badgeFontSize } = resolveAttachBadgeMetrics(size, extText);

    // File-name text: grows slightly with `size`, in viewBox units (the viewBox then scales
    // them up with the rendered SVG height). A gentle slope + an 8-unit cap keep the name
    // from getting too big on the large presets (xl/xxl). ~7 at xs up to the 8 cap.
    const nameFontSize = Math.min(8, 7 + (height - 40) * 0.025);
    const nameBoxHeight = Math.ceil(nameFontSize) + 3;
    const nameBoxY = 40 - nameBoxHeight - 1;

    return (
        // A wrapper div owns the border + rounded corners; overflow:hidden clips the SVG
        // (incl. the square top-left ext badge) so the WHOLE card has rounded corners.
        <div className={classWrap}
            style={{
                display: 'inline-flex',
                width,
                height,
                border: '1px solid #E8E4D4',
                overflow: 'hidden',
                boxSizing: 'border-box'
            }}
        >
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 56 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="56" height="40" fill="#FAF9F1" />

            {/* Paper + paperclip + preview graphic, shifted up and to the right as a group */}
            <g transform="translate(4, -4)">
            {/* Paper sheet (kept from the original icon) */}
            <rect x="30.7261" y="15.5811" width="17.4275" height="12.4189" fill="#D9D9D9" />
            <g filter="url(#filter0_d_attach_thumb)">
                <mask id="path-4-inside-1_attach_thumb" fill="white">
                    <path d="M30 14.8911H47.4275V27.31H30V14.8911Z" />
                </mask>
                <path d="M30 14.8911H47.4275V27.31H30V14.8911Z" fill="#FEFEFE" />
                <path
                    d="M47.4275 27.31V27.51H47.6275V27.31H47.4275ZM30 27.31H29.8V27.51H30V27.31ZM47.4275 14.8911H47.2275V27.31H47.4275H47.6275V14.8911H47.4275ZM47.4275 27.31V27.11H30V27.31V27.51H47.4275V27.31ZM30 27.31H30.2V14.8911H30H29.8V27.31H30Z"
                    fill="#B2B2B2"
                    mask="url(#path-4-inside-1_attach_thumb)"
                />
            </g>
            <line
                y1="-0.1"
                x2="8.01321"
                y2="-0.1"
                transform="matrix(0.72495 0.688801 -0.72495 0.688801 41.6183 9.37158)"
                stroke="#B2B2B2"
                strokeWidth="0.2"
            />
            <path
                d="M38.0455 18.4101L41.7516 9.49094L47.4328 14.8889L38.0455 18.4101Z"
                fill="white"
            />
            <line
                x1="47.4276"
                y1="14.9911"
                x2="41.6184"
                y2="14.9911"
                stroke="#B2B2B2"
                strokeWidth="0.2"
            />
            <line
                x1="41.7183"
                y1="9.37158"
                x2="41.7183"
                y2="14.8911"
                stroke="#B2B2B2"
                strokeWidth="0.2"
            />
            <mask id="path-10-inside-2_attach_thumb" fill="white">
                <path d="M30 9.3717H41.6184V27.3101H30V9.3717Z" />
            </mask>
            <path d="M30 9.3717H41.6184V27.3101H30V9.3717Z" fill="#FEFEFE" />
            <path
                d="M30 9.3717V9.1717H29.8V9.3717H30ZM30 27.3101H29.8V27.5101H30V27.3101ZM30 9.3717V9.5717H41.6184V9.3717V9.1717H30V9.3717ZM41.6184 27.3101V27.1101H30V27.3101V27.5101H41.6184V27.3101ZM30 27.3101H30.2V9.3717H30H29.8V27.3101H30Z"
                fill="#B2B2B2"
                mask="url(#path-10-inside-2_attach_thumb)"
            />
            
            <line x1="32" y1="11.75" x2="40" y2="11.75" stroke="#B2B2B2" strokeWidth="0.5" />
            <line x1="32" y1="13.75" x2="40" y2="13.75" stroke="#B2B2B2" strokeWidth="0.5" />
            <line x1="32" y1="15.75" x2="40" y2="15.75" stroke="#B2B2B2" strokeWidth="0.5" />
            <line x1="32" y1="17.75" x2="45" y2="17.75" stroke="#B2B2B2" strokeWidth="0.5" />
            <line x1="32" y1="19.75" x2="45" y2="19.75" stroke="#B2B2B2" strokeWidth="0.5" />
            <line x1="32" y1="21.75" x2="45" y2="21.75" stroke="#B2B2B2" strokeWidth="0.5" />
            <line x1="32" y1="23.75" x2="45" y2="23.75" stroke="#B2B2B2" strokeWidth="0.5" />
            <line x1="32" y1="25.75" x2="45" y2="25.75" stroke="#B2B2B2" strokeWidth="0.5" />
            <rect x="31.9001" y="20.5819" width="4.41813" height="4.41813" fill="#FF8EAB" />
            <circle cx="37.4228" cy="22.4228" r="2.57724" fill="#FEFE95" />
            <path d="M35.5818 17.6365L38.1326 22.0546H33.031L35.5818 17.6365Z" fill="#8EABFF" />
            
            {/* Paperclip graphic on the sheet (kept from the original icon) */}
            <path
                d="M45.803 9.83254L45.0418 9.07204L41.2355 12.8775C41.0856 13.0275 40.9667 13.2055 40.8856 13.4014C40.8045 13.5973 40.7628 13.8073 40.7628 14.0193C40.7628 14.2313 40.8046 14.4413 40.8858 14.6372C40.967 14.833 41.0859 15.011 41.2359 15.1609C41.3858 15.3108 41.5638 15.4297 41.7597 15.5108C41.9557 15.5919 42.1656 15.6337 42.3776 15.6336C42.5897 15.6336 42.7996 15.5918 42.9955 15.5106C43.1914 15.4295 43.3694 15.3105 43.5193 15.1605L48.0868 10.5938C48.5914 10.089 48.8749 9.40451 48.8748 8.69077C48.8747 7.97703 48.5911 7.29255 48.0864 6.78791C47.5816 6.28327 46.8971 5.99981 46.1834 5.99988C45.4696 5.99995 44.7851 6.28355 44.2805 6.78829L39.485 11.583L39.4745 11.5928C38.0083 13.059 38.0083 15.435 39.4745 16.9005C40.9408 18.366 43.3168 18.366 44.783 16.9005L44.7928 16.89L44.7935 16.8908L48.0673 13.6178L47.306 12.8573L44.0323 16.1295L44.0225 16.1393C43.5201 16.6407 42.8393 16.9223 42.1295 16.9223C41.4197 16.9223 40.7389 16.6407 40.2365 16.1393C39.9873 15.8898 39.7898 15.5936 39.6554 15.2676C39.521 14.9416 39.4523 14.5922 39.4533 14.2396C39.4543 13.887 39.5249 13.538 39.6611 13.2128C39.7973 12.8875 39.9964 12.5924 40.247 12.3443L40.2463 12.3435L45.0425 7.54879C45.6718 6.91879 46.6963 6.91879 47.3263 7.54879C47.9563 8.17879 47.9555 9.20254 47.3263 9.83179L42.7588 14.3985C42.6565 14.4926 42.5219 14.5435 42.3831 14.5406C42.2442 14.5377 42.1119 14.4813 42.0136 14.3832C41.9154 14.285 41.8588 14.1527 41.8558 14.0138C41.8528 13.875 41.9036 13.7403 41.9975 13.638L45.8038 9.83179L45.803 9.83254Z"
                fill="#888888"
            />
            </g>

            {/* Top-left file-type badge (ext); scaled down from md upward so it stays
                proportional instead of looking oversized on larger sizes.
                NOTE: the label is centered via `dominantBaseline: 'middle'` (in
                ATTACH_BADGE.TEXT_PROPS). SVG `dominant-baseline` can render with a slight
                vertical offset across browsers; if a label ever looks off-center on a
                specific browser, nudge `ATTACH_BADGE.TEXT_PROPS.y` instead of relying on it. */}
            <g transform={`scale(${badgeScale})`}>
                <path d={ATTACH_BADGE.PATH} fill={extColor} />
                <text {...ATTACH_BADGE.TEXT_PROPS} fontSize={badgeFontSize}>
                    {extText}
                </text>
            </g>

            {/* File name in the lower area. A foreignObject hosts an HTML div so we get a
                real pixel-based ellipsis (SVG <text> has no text-overflow). It scales with
                the SVG because the viewBox maps these units to the rendered size. */}
            {fileName && (
                <foreignObject x="1.5" y={nameBoxY} width="53" height={nameBoxHeight}>
                    <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        style={{
                            fontFamily: FONT_FAMILY,
                            fontSize: nameFontSize,
                            fontWeight: 700,
                            color: '#2E3138',
                            lineHeight: `${nameBoxHeight}px`,
                            letterSpacing: '-0.3px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {fileName}
                    </div>
                </foreignObject>
            )}

            <defs>
                <filter
                    id="filter0_d_attach_thumb"
                    x="26"
                    y="10.8911"
                    width="25.4276"
                    height="20.4189"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset />
                    <feGaussianBlur stdDeviation="2" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                    />
                    <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_attach_thumb"
                    />
                    <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_attach_thumb"
                        result="shape"
                    />
                </filter>
            </defs>
        </svg>
        </div>
    );
}

AttachFileThumbnailIcon.propTypes = {
    fileName: PropTypes.string,
    size: PropTypes.oneOfType([
        PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
        PropTypes.number
    ]),
    classWrap: PropTypes.string,
    badgeText: PropTypes.string,
    badgeColor: PropTypes.string
};

export default AttachFileThumbnailIcon;
